import { DragEvent, HTMLAttributes, useEffect, useMemo, useState } from 'react';

import { envVariables } from '@/utils/env';
import { PLANK_TO_MWD_DRAG_DATA_TYPE } from '@/utils/plankToMwdDrag';
import { startPlankToMwdDrag } from '@/utils/plankToMwdDragLifecycle';

import { PlankSelectionItem } from '../state/types/PlankSelectionStore';
import { buildPlankToMwdDragPayload } from '../utils/plankSelection';

import usePlankItemSelection from './usePlankItemSelection';

type PlankDragSourceOptions = {
  /** Identifies this row in the Plank multi-selection and in the drag payload, or undefined
   * when the row cannot be dragged to MWD (e.g. a conflict without a werklijnId). */
  item: PlankSelectionItem | undefined;
  /** Value for the row's data-testid, e.g. `draggableDefect_WO-1`. */
  testId: string;
  /** Writes the DataTransfer payload for the drop target inside the Plank itself (currently
   * only defect -> onderhoud). When omitted the row is not draggable while the flag is off. */
  writeInPlankDragData?: (dataTransfer: DataTransfer) => void;
  /** Whether this row is currently open in the sidepanel (shown with a focus border). Combined
   * with isSelected to decide whether to show the grab/grabbing cursor. */
  isFocused?: boolean;
};

type PlankDragSource = {
  isSelected: boolean;
  /** Whether the grab/grabbing cursor should be shown: the row is draggable and either selected
   * or focused. Pressing the mouse down on a focused row momentarily closes the sidepanel (its
   * "click outside" handler fires on mousedown, before any drag can start), which would flip
   * `isFocused` to false right as the user starts to grab the row. This latches "was
   * selected/focused at press-time" for the duration of the press so the cursor doesn't revert. */
  showDragCursor: boolean;
  /** Spread onto the row's drag handle element (the card). */
  dragProps: Partial<HTMLAttributes<HTMLDivElement>>;
  /** Capture-phase click handler implementing Ctrl/Shift multi-select, or undefined when the
   * feature is disabled. */
  onSelectionClickCapture: ((event: React.MouseEvent<HTMLElement>) => void) | undefined;
};

/** Shared drag/multi-select behavior for the draggable Plank rows (defect, conflict, reserve):
 * the feature-flag gate, the drag payload (a batch payload when the row is part of a
 * multi-selection), the drag start/end broadcast, and Ctrl/Shift multi-select.
 */
export default function usePlankDragSource({
  item,
  testId,
  writeInPlankDragData,
  isFocused = false,
}: PlankDragSourceOptions): PlankDragSource {
  const featurePlankMwdDragDrop = !!envVariables().featurePlankMwdDragDrop;
  const { isSelected, handleSelectionClickCapture } = usePlankItemSelection(item, featurePlankMwdDragDrop);

  // Latches the cursor "on" for the duration of a press that started on a selected/focused row,
  // surviving the sidepanel's transient close (see showDragCursor doc above).
  const [isPressLocked, setIsPressLocked] = useState(false);
  const handleMouseDown = () => {
    if (isSelected || isFocused) setIsPressLocked(true);
  };
  const releasePressLock = () => setIsPressLocked(false);

  // Releasing the mouse over a different element (e.g. an adjacent row, or anywhere else) never
  // fires this row's own onMouseUp, and onDragEnd never fires when no native drag actually
  // started. Without a global fallback, the lock could stay stuck "on" after such a press.
  useEffect(() => {
    if (!isPressLocked) return undefined;
    globalThis.addEventListener('mouseup', releasePressLock);
    return () => globalThis.removeEventListener('mouseup', releasePressLock);
  }, [isPressLocked]);

  const dragProps = useMemo<Partial<HTMLAttributes<HTMLDivElement>>>(() => {
    // Feature off: the row only stays draggable for the in-Plank drop, if it has one.
    if (!featurePlankMwdDragDrop) {
      if (!writeInPlankDragData) return {};
      return {
        draggable: true,
        onMouseDown: handleMouseDown,
        onMouseUp: releasePressLock,
        onDragStart: (event: DragEvent<HTMLDivElement>) => {
          event.dataTransfer.effectAllowed = 'move';
          writeInPlankDragData(event.dataTransfer);
        },
        onDragEnd: releasePressLock,
        'data-testid': testId,
      };
    }

    if (!item) return {};

    return {
      draggable: true,
      onMouseDown: handleMouseDown,
      onMouseUp: releasePressLock,
      onDragStart: (event: DragEvent<HTMLDivElement>) => {
        const payload = buildPlankToMwdDragPayload(item);

        event.dataTransfer.effectAllowed = writeInPlankDragData ? 'copyMove' : 'copy';
        event.dataTransfer.setData(PLANK_TO_MWD_DRAG_DATA_TYPE, JSON.stringify(payload));
        writeInPlankDragData?.(event.dataTransfer);

        startPlankToMwdDrag(event.currentTarget, payload);
      },
      // Transport completion is native; this React handler only resets mounted-row UI.
      onDragEnd: releasePressLock,
      'data-testid': testId,
    };
  }, [featurePlankMwdDragDrop, isFocused, isSelected, item, testId, writeInPlankDragData]);

  return {
    isSelected,
    showDragCursor: !!dragProps.draggable && (isSelected || isFocused || isPressLocked),
    dragProps,
    onSelectionClickCapture: featurePlankMwdDragDrop ? handleSelectionClickCapture : undefined,
  };
}
